/**
 * Impact-tolerance declarations inline section (P201).
 *
 * Renders inside the IBS admin drawer's extraSections row. Operators see the
 * declaration history per IBS, can create a new draft + sign off (CRO +
 * Business Owner). Once both signoffs land, the parent
 * IBSService.impact_tolerance_hours updates atomically (backend) -- the
 * war-room's effective tolerance, and what the P200 signed pack evidences.
 *
 * Mirrors serviceClassificationDecisions.tsx; admin-gated (a realm-role gate
 * is a deferred follow-on, as classification was at P73).
 */
import { useTranslation } from "react-i18next";
import { useMemo, useState, type FormEvent } from "react";

import { Button } from "shared/components/Button";
import { useMe } from "shared/api/useMe";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { workKeys } from "shared/work/useMyWork";

import { api } from "shared/api/client";
import { useToast } from "shared/toast/ToastProvider";
import { canRunOperations } from "shared/api/authority";

type WorkflowState = "draft" | "cro_signed" | "business_owner_signed" | "approved";

const STATE_LABELS: Record<WorkflowState, string> = {
  draft: "Draft",
  cro_signed: "CRO signed",
  business_owner_signed: "Business Owner signed",
  approved: "Approved",
};

const STATE_TONES: Record<WorkflowState, string> = {
  draft: "bg-slate-100 text-slate-700",
  cro_signed: "bg-amber-100 text-amber-900",
  business_owner_signed: "bg-amber-100 text-amber-900",
  approved: "bg-emerald-100 text-emerald-800",
};

interface Declaration {
  id: string;
  ibs_service_id: string;
  declared_tolerance_hours: number;
  rationale: string;
  declared_by_name: string | null;
  decided_at: string;
  cro_signoff_name: string | null;
  cro_signoff_email: string | null;
  cro_signoff_at: string | null;
  business_owner_signoff_name: string | null;
  business_owner_signoff_email: string | null;
  business_owner_signoff_at: string | null;
  workflow_state: WorkflowState;
  created_at: string;
  updated_at: string;
}

interface DeclarationDraft {
  declared_tolerance_hours: number;
  rationale: string;
  declared_by_name: string;
  /**
   * P402: the other declared dimensions. Blank means NOT DECLARED --
   * which is different from a threshold of zero, so a blank field must
   * never be sent as 0.
   */
  customers_affected: string;
  financial_loss_sar: string;
  transactions_lost: string;
}

function emptyDraft(): DeclarationDraft {
  return {
    declared_tolerance_hours: 4,
    rationale: "",
    declared_by_name: "",
    customers_affected: "",
    financial_loss_sar: "",
    transactions_lost: "",
  };
}

/** P402: the dimensions capturable here, with their units. */
const EXTRA_METRICS = [
  { key: "customers_affected", label: "Customers affected", unit: "customers" },
  { key: "financial_loss_sar", label: "Financial loss", unit: "SAR" },
  { key: "transactions_lost", label: "Transactions lost", unit: "transactions" },
] as const;

interface SignoffDraft {
  name: string;
  email: string;
}

function useDeclarations(ibsId: string) {
  return useQuery<Declaration[]>({
    queryKey: ["impact-tolerance-declarations", ibsId] as const,
    queryFn: () =>
      api.get<Declaration[]>(
        `/v1/admin/foundation/ibses/${ibsId}/impact-tolerance-declarations`,
      ),
    enabled: Boolean(ibsId),
  });
}

export function ImpactToleranceDeclarationsSection({ rowId }: { rowId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useDeclarations(rowId);
  const me = useMe();
  // P237.1: best-effort affordance -- admin OR the attestation realm role
  // (cro / business_owner). The backend gate is the precise enforcement
  // (MARSAD_IMPACT_TOLERANCE_POSITION_GATE); when off, admin works.
  const canSignRole = (role: "cro" | "business-owner"): boolean => {
    if (canRunOperations(me.data)) return true;
    const slug = role === "cro" ? "cro" : "business_owner";
    return me.data?.realm_roles?.includes(slug) ?? false;
  };
  const qc = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [signing, setSigning] = useState<{
    decisionId: string;
    role: "cro" | "business-owner";
  } | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["impact-tolerance-declarations", rowId] });
    /* Round 11 D2 -- and the inbox that answers "where did my work
       go". The declarer's My Work is the surface built for exactly
       this moment, and it was not told the moment had happened: she
       declared, went to look, and the band showed her nothing until
       some later session refetched it. Fifth round for this finding,
       and refetching the one query that answers it is the half that
       makes the line appear the moment she looks. */
    qc.invalidateQueries({ queryKey: workKeys.mine });
  };

  const createDraft = useMutation({
    mutationFn: async (draft: DeclarationDraft) => {
      const body: Record<string, unknown> = {
        declared_tolerance_hours: draft.declared_tolerance_hours,
        rationale: draft.rationale,
        decided_at: new Date().toISOString(),
      };
      if (draft.declared_by_name) body.declared_by_name = draft.declared_by_name;
      // Only NON-BLANK dimensions are sent. A blank field means the
      // bank has not declared that dimension, and sending 0 would
      // declare a tolerance of nothing, which every projection breaches.
      const metrics: Record<string, string> = {};
      for (const m of EXTRA_METRICS) {
        const raw = draft[m.key].trim();
        if (raw) metrics[m.key] = raw;
      }
      if (Object.keys(metrics).length > 0) body.declared_metrics = metrics;
      return await api.post<Declaration>(
        `/v1/admin/foundation/ibses/${rowId}/impact-tolerance-declarations`,
        body,
      );
    },
    onSuccess: () => {
      setCreating(false);
      void invalidate();
    },
  });

  const signoff = useMutation({
    mutationFn: async (input: {
      decisionId: string;
      role: "cro" | "business-owner";
      body: SignoffDraft;
    }) => {
      const path = `/v1/admin/foundation/impact-tolerance-declarations/${input.decisionId}/signoff-${input.role}`;
      return await api.patch<Declaration>(path, input.body);
    },
    onSuccess: (decl, input) => {
      setSigning(null);
      void invalidate();
      /* Round 17 G20 -- a leg that took seconds ended with the button
         quietly reverting. Say it landed, and what it now waits on. */
      toast.push({
        tone: "success",
        message:
          decl.workflow_state === "approved"
            ? t("screens.impactToleranceDeclarations.signedApproved", {
                hours: decl.declared_tolerance_hours,
              })
            : t(
                input.role === "cro"
                  ? "screens.impactToleranceDeclarations.signedCro"
                  : "screens.impactToleranceDeclarations.signedOwner",
                { hours: decl.declared_tolerance_hours },
              ),
      });
    },
  });

  const deleteDraft = useMutation({
    mutationFn: async (decisionId: string) => {
      await api.delete(
        `/v1/admin/foundation/impact-tolerance-declarations/${decisionId}`,
      );
    },
    onSuccess: () => void invalidate(),
  });

  const declarations = useMemo(() => data ?? [], [data]);
  // C1 -- the form invited another draft while one was already in
  // flight, so four identical declarations accumulated and nobody
  // could tell which one the signatures would land on. One at a time:
  // supersede it by taking it through the ceremony, or delete it.
  const inFlight = declarations.find(
    (d) => d.cro_signoff_at === null || d.business_owner_signoff_at === null,
  );

  return (
    <section
      data-testid="impact-tolerance-declarations"
      className="space-y-3 border-t border-slate-200 pt-4 mt-4"
    >
      <header className="flex items-baseline justify-between gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("screens.impactToleranceDeclarations.impactToleranceDeclarations")}
        </h3>
        {inFlight ? (
          <span data-testid="itd-new-blocked" className="text-xs text-slate-500">
            {t("screens.impactToleranceDeclarations.oneInFlight")}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="itd-new-button"
            className="text-xs font-medium text-brand-navy hover:text-brand-brass"
          >
            {t("screens.impactToleranceDeclarations.newDeclaration")}
          </button>
        )}
      </header>

      {isLoading && (
        <p className="text-xs text-slate-500">
          {t("screens.impactToleranceDeclarations.loadingDeclarations")}
        </p>
      )}

      {!isLoading && declarations.length === 0 && (
        <p data-testid="itd-empty" className="text-xs italic text-slate-500">
          {t(
            "screens.impactToleranceDeclarations.noImpactToleranceDeclarationsOnThisIbs",
          )}
        </p>
      )}

      <ul className="space-y-2">
        {declarations.map((d) => (
          <li
            key={d.id}
            data-testid={`itd-row-${d.id}`}
            className="rounded-md border border-slate-200 bg-white p-3 text-sm"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-slate-900">
                {d.declared_tolerance_hours}
                {t("screens.impactToleranceDeclarations.hTolerance")}
              </span>
              <span
                data-testid={`itd-row-state-${d.id}`}
                data-state={d.workflow_state}
                className={[
                  "inline-flex items-center rounded-full px-2 py-0.5",
                  "text-xs font-medium uppercase tracking-wide",
                  STATE_TONES[d.workflow_state],
                ].join(" ")}
              >
                {STATE_LABELS[d.workflow_state]}
              </span>
              {d.workflow_state === "draft" && (
                <Button
                  variant="danger"
                  onClick={() => deleteDraft.mutate(d.id)}
                  testid={`itd-delete-${d.id}`}
                  className="ms-auto"
                >
                  {t("screens.impactToleranceDeclarations.deleteDraft")}
                </Button>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-600">{d.rationale}</p>
            <p className="mt-1 text-[11px] text-slate-500">
              {t("screens.impactToleranceDeclarations.decided")}{" "}
              {d.decided_at.slice(0, 10)}
              {d.declared_by_name ? ` · declared by: ${d.declared_by_name}` : ""}
            </p>

            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <SignoffCell
                role="cro"
                label="CRO"
                name={d.cro_signoff_name}
                email={d.cro_signoff_email}
                at={d.cro_signoff_at}
                disabled={d.workflow_state === "approved" || d.cro_signoff_at != null}
                canSign={canSignRole("cro")}
                onClick={() => setSigning({ decisionId: d.id, role: "cro" })}
              />
              <SignoffCell
                role="business-owner"
                label="Business Owner"
                name={d.business_owner_signoff_name}
                email={d.business_owner_signoff_email}
                at={d.business_owner_signoff_at}
                disabled={
                  d.workflow_state === "approved" || d.business_owner_signoff_at != null
                }
                canSign={canSignRole("business-owner")}
                onClick={() => setSigning({ decisionId: d.id, role: "business-owner" })}
              />
            </div>
          </li>
        ))}
      </ul>

      {creating && (
        <NewDeclarationForm
          onCancel={() => setCreating(false)}
          onSubmit={(draft) => createDraft.mutate(draft)}
          submitting={createDraft.isPending}
          error={createDraft.error?.message ?? null}
        />
      )}

      {signing && (
        <SignoffModal
          role={signing.role}
          onCancel={() => setSigning(null)}
          onSubmit={(body) =>
            signoff.mutate({
              decisionId: signing.decisionId,
              role: signing.role,
              body,
            })
          }
          submitting={signoff.isPending}
          error={signoff.error?.message ?? null}
        />
      )}
    </section>
  );
}

interface SignoffCellProps {
  role: "cro" | "business-owner";
  label: string;
  name: string | null;
  email: string | null;
  at: string | null;
  disabled: boolean;
  canSign: boolean;
  onClick: () => void;
}

function SignoffCell({
  role,
  label,
  name,
  email,
  at,
  disabled,
  canSign,
  onClick,
}: SignoffCellProps) {
  const { t } = useTranslation();
  if (at && name && email) {
    return (
      <div
        data-testid={`itd-signoff-${role}-signed`}
        className="rounded-md bg-emerald-50 border border-emerald-200 p-2 text-emerald-900"
      >
        <span className="block text-[10px] uppercase tracking-wide">
          {label} {t("screens.impactToleranceDeclarations.signed")}
        </span>
        <span className="block">{name}</span>
        <span className="block text-[11px] opacity-80">{email}</span>
        <span className="block text-[11px] opacity-80">
          {at.slice(0, 16).replace("T", " ")}
        </span>
      </div>
    );
  }
  if (!canSign) {
    return (
      <div
        data-testid={`itd-signoff-${role}-locked`}
        className="rounded-md border border-slate-200 bg-slate-50 p-2 text-slate-400"
      >
        <span className="block text-[10px] uppercase tracking-wide">{label}</span>
        <span className="block text-[11px]">
          {t("screens.impactToleranceDeclarations.adminRequiredToSignOff")}
        </span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`itd-signoff-${role}-button`}
      className={[
        "rounded-md border p-2 text-start",
        disabled
          ? "border-slate-200 text-slate-400 cursor-not-allowed bg-slate-50"
          : "border-amber-200 text-amber-900 bg-amber-50 hover:bg-amber-100",
      ].join(" ")}
    >
      <span className="block text-[10px] uppercase tracking-wide">{label}</span>
      <span className="block font-medium">
        {t("screens.impactToleranceDeclarations.signOff")}
      </span>
    </button>
  );
}

interface NewDeclarationFormProps {
  onCancel: () => void;
  onSubmit: (draft: DeclarationDraft) => void;
  submitting: boolean;
  error: string | null;
}

function NewDeclarationForm({
  onCancel,
  onSubmit,
  submitting,
  error,
}: NewDeclarationFormProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DeclarationDraft>(emptyDraft);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(draft);
  };

  return (
    <form
      onSubmit={submit}
      data-testid="itd-new-form"
      className="rounded-md border border-indigo-200 bg-indigo-50 p-3 space-y-2 text-sm"
    >
      <div>
        <label className="block text-xs font-semibold text-slate-700">
          {t("screens.impactToleranceDeclarations.declaredToleranceHours")}
        </label>
        <input
          type="number"
          min={1}
          value={draft.declared_tolerance_hours}
          onChange={(e) =>
            setDraft({
              ...draft,
              declared_tolerance_hours: Number(e.target.value),
            })
          }
          required
          data-testid="itd-new-hours"
          className="w-full rounded border-slate-300 text-sm"
        />
      </div>
      <div data-testid="itd-new-metrics" className="space-y-1">
        <p className="text-xs font-semibold text-slate-700">
          {t("screens.impactToleranceDeclarations.otherDeclaredDimensionsOptional")}
        </p>
        <p className="text-[11px] text-slate-500">
          {t("screens.impactToleranceDeclarations.leaveBlankToDeclareNoToleranceOn")}
        </p>
        {EXTRA_METRICS.map((m) => (
          <div key={m.key} className="flex items-center gap-2">
            <label className="w-40 shrink-0 text-xs text-slate-600">{m.label}</label>
            <input
              type="number"
              min={0}
              step="any"
              value={draft[m.key]}
              onChange={(e) => setDraft({ ...draft, [m.key]: e.target.value })}
              data-testid={`itd-new-${m.key}`}
              className="flex-1 rounded border-slate-300 text-sm"
            />
            <span className="w-24 shrink-0 text-[11px] text-slate-400">{m.unit}</span>
          </div>
        ))}
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-700">
          {t("screens.impactToleranceDeclarations.rationaleRequired")}
        </label>
        <textarea
          dir="auto"
          value={draft.rationale}
          onChange={(e) => setDraft({ ...draft, rationale: e.target.value })}
          required
          rows={3}
          data-testid="itd-new-rationale"
          className="w-full rounded border-slate-300 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-700">
          {t("screens.impactToleranceDeclarations.declaredByOptional")}
        </label>
        <input
          dir="auto"
          type="text"
          value={draft.declared_by_name}
          onChange={(e) => setDraft({ ...draft, declared_by_name: e.target.value })}
          className="w-full rounded border-slate-300 text-sm"
        />
      </div>
      {error && (
        <p data-testid="itd-new-error" className="text-xs text-rose-700">
          {error}
        </p>
      )}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 text-xs text-slate-700 hover:text-slate-900"
        >
          {t("screens.impactToleranceDeclarations.cancel")}
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="itd-new-submit"
          className="px-3 py-1 text-xs font-medium rounded bg-brand-navy text-white hover:bg-brand-navyDeep disabled:opacity-60"
        >
          {submitting
            ? t("screens.impactToleranceDeclarations.creating")
            : t("screens.impactToleranceDeclarations.createDraft")}
        </button>
      </div>
    </form>
  );
}

interface SignoffModalProps {
  role: "cro" | "business-owner";
  onCancel: () => void;
  onSubmit: (body: SignoffDraft) => void;
  submitting: boolean;
  error: string | null;
}

function SignoffModal({
  role,
  onCancel,
  onSubmit,
  submitting,
  error,
}: SignoffModalProps) {
  const { t } = useTranslation();
  const me = useMe();
  // B3 -- one signing rule per ceremony: the session signs, here as in
  // the inbox. See the note in serviceClassificationDecisions.tsx.
  const signerName = me.data?.display_name ?? me.data?.username ?? "";
  const signerEmail = me.data?.email ?? "";
  const attributable = signerName !== "" && signerEmail !== "";
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit({ name: signerName, email: signerEmail });
  };
  const label = role === "cro" ? "CRO" : "Business Owner";

  return (
    <form
      onSubmit={submit}
      data-testid={`itd-signoff-form-${role}`}
      className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2 text-sm"
    >
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
        {t("screens.impactToleranceDeclarations.signOffAs")} {label}
      </h4>
      {attributable ? (
        <p data-testid={`itd-signoff-${role}-signer`} className="text-xs">
          {t("screens.serviceClassificationDecisions.signingAs", {
            name: signerName,
            email: signerEmail,
          })}
        </p>
      ) : (
        <p
          data-testid={`itd-signoff-${role}-unattributable`}
          className="text-xs text-amber-900"
        >
          {t("myWork.action.noIdentity")}
        </p>
      )}
      {error && (
        <p data-testid={`itd-signoff-${role}-error`} className="text-xs text-rose-700">
          {error}
        </p>
      )}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 text-xs text-slate-700 hover:text-slate-900"
        >
          {t("screens.impactToleranceDeclarations.cancel")}
        </button>
        <button
          type="submit"
          disabled={submitting || !attributable}
          data-testid={`itd-signoff-${role}-submit`}
          className="px-3 py-1 text-xs font-medium rounded bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-60"
        >
          {submitting
            ? t("screens.impactToleranceDeclarations.signing")
            : `Confirm ${label} signoff`}
        </button>
      </div>
    </form>
  );
}
